import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const UpdatedClassCart = ({
  classesInCart,
  onRemoveClass,
  onAddCourses,
  yearNumber,
  semester, // If provided, we show a simpler button
  surveyAnswers,
  termMap,
  finalizedCourseIds,
}) => {
  const { t } = useTranslation();

  // Safely parse the start year
  const startYear = parseInt(surveyAnswers?.SchoolStart?.year, 10) || 0;

  // Calculate the specific year for display when semester is provided
  const specificYear =
    semester === 'Fall'
      ? startYear + yearNumber - 1
      : startYear + yearNumber;

  // Safely compute numYears from finalizedCourseIds
  const safeNumYears =
    typeof finalizedCourseIds?.numYears === 'number'
      ? finalizedCourseIds.numYears
      : 0;
  const numYearsSelection = safeNumYears + 1; // e.g. if 4 years, we want years from StartYear..StartYear+4

  // Local state for manually selected year & term
  const [selectedYear, setSelectedYear] = useState(specificYear || '');
  const [selectedTerm, setSelectedTerm] = useState(semester || '');

  /**
   * handleAddCoursesRegular:
   * If a semester is *already* provided (e.g. "Fall"), we just call onAddCourses
   * with the given yearNumber and semester. You can adapt to your code’s logic
   * if you prefer the integer-based approach instead.
   */
  const handleAddCoursesRegular = () => {
    onAddCourses(yearNumber, semester);
  };

  /**
   * handleAddCoursesSelected:
   * If a semester is *not* provided, we rely on the user to pick from the dropdowns.
   * Here we pass the user-chosen (selectedYear, selectedTerm) to onAddCourses.
   */
  const handleAddCoursesSelected = () => {
    if (!selectedTerm || !selectedYear) {
      toast.warning(
        t('classCart.selectYearSemesterWarning',
          "Please select the year and semester to which you'd like to add the courses.")
      );
      return;
    }

    // Example offset logic if you want “Fall => year+1, Spring => year” or something else.
    // Otherwise, keep it simple and pass them directly:
    onAddCourses(selectedYear - startYear + 1, selectedTerm);

    // If you prefer to store S1 or S2 internally, you can do a little mapping here:
    // e.g. if (selectedTerm === "Fall") => "S1"
  };

  return (
    <div
      className={`py-3 ${
        !semester || semester.trim() === '' ? 'px-2' : 'px-3'
      } bg-gray-50 shadow-md border rounded-lg w-full min-h-200 mb-20`}
    >
      <h5 className="text-lg text-center font-bold mb-4">
        {t('classCart.title', 'Class Cart')}
      </h5>

      {classesInCart.length === 0 ? (
        <p className="text-md text-center">
          {t('classCart.addClassesPrompt', 'Add classes to the cart')}
        </p>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          {classesInCart.map((classData, index) => (
            <ClassCartItem
              key={index}
              classData={classData}
              onRemoveClass={onRemoveClass}
            />
          ))}
        </div>
      )}

      <div className="flex justify-center mt-2">
        {/* If NO semester prop was passed, show dynamic dropdowns */}
        {!semester || semester.trim() === '' ? (
          <button
            className="w-full bg-blue-500 hover:bg-blue-700 text-white text-xs py-2 rounded mt-1 flex items-center justify-center space-x-2"
            onClick={handleAddCoursesSelected}
          >
            <span>{t('classCart.addTo', 'Add to')}</span>

            {/* Dynamic Term Dropdown */}
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              className="bg-gray-100 text-gray-800 text-xs border-none outline-none appearance-none w-24 rounded px-2 py-1 text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="" disabled hidden>
                {t('classCart.semesterPlaceholder', 'Term')}
              </option>
              {Object.keys(termMap)
                .sort((a, b) => Number(a) - Number(b))
                .map((key) => {
                  const friendlyName = termMap[key]; // e.g. "Fall", "Spring", "Summer"
                  return (
                    <option key={key} value={friendlyName}>
                      {friendlyName}
                    </option>
                  );
                })}
            </select>

            {/* Year Dropdown */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="bg-gray-100 text-gray-800 text-xs border-none outline-none appearance-none w-16 rounded px-2 py-1 text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="" disabled hidden>
                {t('classCart.yearPlaceholder', 'Year')}
              </option>
              {Number.isInteger(numYearsSelection) && numYearsSelection > 0 ? (
                [...Array(numYearsSelection)].map((_, idx) => {
                  const thisYear = startYear + idx;
                  return (
                    <option key={idx} value={thisYear}>
                      {thisYear}
                    </option>
                  );
                })
              ) : (
                <option disabled>
                  {t('classCart.invalidYears', 'Invalid Years')}
                </option>
              )}
            </select>
          </button>
        ) : (
          // If we *do* have a semester prop, show simpler button
          <button
            className="w-full bg-blue-500 hover:bg-blue-700 text-white text-sm py-2 px-1 rounded mt-1 flex items-center justify-center space-x-2"
            onClick={handleAddCoursesRegular}
          >
            <span>
              {t('classCart.addToSpecific', 'Add to')} {semester} {specificYear}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

const ClassCartItem = ({ classData, onRemoveClass }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const handleHover = (hoverState) => {
    setIsHovered(hoverState);
  };

  return (
    <div
      className="class-cart-item bg-white mb-2 rounded p-2 relative shadow-none"
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      <p className="font-bold">{classData.courseInfo_courseNumber}</p>
      {isHovered && (
        <div className="absolute bottom-2 right-3">
          <button
            className="text-xs text-red-500 hover:text-black"
            onClick={() => onRemoveClass(classData)}
          >
            {t('classCart.remove', 'Remove')}
          </button>
        </div>
      )}
    </div>
  );
};

export default UpdatedClassCart;
